import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { SITE } from "../../lib/site";

const REVIEWS = [
  {
    name: "Alexis Sessini",
    role: "Mariage · Local Guide Google",
    text:
      "Adrien et Marie ont animé notre mariage ce week-end, de leur arrivée jusqu'à leur départ dimanche matin. Nous avons vécu quelque chose d'extraordinaire. Tout est tellement bien réglé et millimétré qu'aucun stress ne nous est parvenu durant ce week-end. Concernant la partie sonorisation, je pense avoir trouvé le Graal : Adrien a réussi à faire danser des gens qui n'avaient même pas dansé à leur propre mariage. Adrien, Marie et Guillaume ont contribué à la réussite de notre mariage. Un grand merci.",
    stars: 5,
  },
  {
    name: "Victoria Dumand",
    role: "Mariage · juillet 2026",
    text:
      "Un immense merci à toi et à ta femme pour avoir animé notre mariage ! Tout était absolument parfait du début à la fin. Tu as su créer une ambiance incroyable et faire danser tout le monde. Grâce à ton professionnalisme, ton énergie et ton talent, cette journée restera gravée dans nos mémoires. Nous te recommandons les yeux fermés !",
    stars: 5,
  },
  {
    name: "Morgan Trillard",
    role: "Mariage · août 2026",
    text:
      "L'équipe est vraiment au top, grâce à eux nous avons vraiment passé un super moment, que ce soit avec la partie photo/vidéo mais également la partie animation/DJ. Ils nous guident tout le long de la journée et c'est très rassurant. Merci à vous Adrien, Marie et Guillaume d'avoir rendu cette journée encore plus inoubliable.",
    stars: 5,
  },
  {
    name: "Bastien Joly",
    role: "Communauté Coulisse DJ",
    text:
      "Je l'ai contacté via les réseaux et il a su répondre correctement à toutes mes questions. Même lorsque la conversation était terminée il m'a relancé : il prend son temps personnel pour sa communauté. Je recommande. DJ super !",
    stars: 5,
  },
];

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} étoiles sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        className={
          i <= n ? "fill-[#D4AF37] text-[#D4AF37]" : "text-zinc-700"
        }
      />
    ))}
  </div>
);

const GoogleG = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
  </svg>
);

export const Reviews = () => {
  return (
    <section
      id="reviews"
      data-testid="google-reviews-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Ils m'ont fait confiance</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Plus de 800 événements réussis, <span className="gold-text-gradient">une réputation</span> bâtie sur le terrain.
            </h2>
            <p className="mt-4 text-zinc-400">
              Mon objectif : une soirée fluide, élégante, festive et mémorable.
              Et les retours de mes clients en sont la meilleure preuve.
            </p>
          </div>

          <div
            data-testid="reviews-summary"
            className="glass rounded-2xl p-6 md:p-7 flex items-center gap-5 min-w-[260px]"
          >
            <div className="flex flex-col items-center gap-2 px-3">
              <GoogleG size={26} />
              <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400">
                Google
              </div>
            </div>
            <div className="h-14 w-px bg-white/10" />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-3xl md:text-4xl font-bold text-white">4,4</span>
                <span className="text-sm text-zinc-400">/ 5</span>
              </div>
              <Stars n={4} />
              <div className="mt-1 text-xs text-zinc-400">277 avis Google vérifiés</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {REVIEWS.map((r, i) => (
            <motion.article
              key={r.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`review-card-${i}`}
              className="glass rounded-2xl p-7 hover:-translate-y-1 transition-transform duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <Stars n={r.stars} />
                <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-zinc-400">
                  <GoogleG size={14} /> Avis vérifié
                </span>
              </div>
              <p className="mt-5 text-zinc-200 leading-relaxed text-[15px] flex-1">
                « {r.text} »
              </p>
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="text-white font-semibold text-sm">{r.name}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{r.role}</div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <a
            href={SITE.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-google-reviews-btn"
            className="btn-outline-gold rounded-full px-7 py-4 text-sm inline-flex items-center gap-2"
          >
            Voir tous les avis Google <ExternalLink size={15} />
          </a>
        </div>
      </div>
    </section>
  );
};
